import { FunctionComponent, useMemo, useState } from "react";
import "./CSMShopfloorHeader.css";
import { useStoreActions, useStoreState } from "../../store";

import LOGO_SRC from "../../Icons/Logo.svg";
import INFO_SRC from "../../Icons/Info.svg";
import USER_SRC from "../../Icons/user.svg";
import SYSTEM_SRC from "../../Icons/gear.svg";
import IMAGE_SRC from "../../Icons/image.svg";

import { useLocation, useNavigate } from "react-router";
import {Tooltip} from "../../components/Tooltip";
import DOC_SRC from "../../Icons/documentIcon.svg"
import { isAnalyticsEnabled } from "../../utility/analyticsAccess";
import { userIsAnalyticsOnly } from "../../utility/userRole";
import { getAppVersionLabel } from "../../utility/appVersion";
import ScreenHelpButton from "../SupportAssistant/ScreenHelpButton";




let defaultOrganization: string | null = null;

type CSMShopfloorHeaderProps = {
  onLogoContainerClick?: () => void;
};

export function setDefaultOrganization(organization: string) {
  defaultOrganization = organization;
}

/**
 * The organization the session is currently connected to (what the header shows), or null
 * before it has been established. Read by the org switcher so it opens on the CURRENT org
 * rather than always on CORP — with the auto-tenant feature a CORP user can start the
 * session in another org, and an unchanged Submit must not silently move them.
 */
export function getDefaultOrganization(): string | null {
  return defaultOrganization;
}



// Bit flags persisted in UserDetails.Complexity
const MANAGER_BIT = 1 << 0;   // 1
const SHOPFLOOR_BIT = 1 << 1; // 2

const CSMShopfloorHeader: FunctionComponent<CSMShopfloorHeaderProps> = ({
  onLogoContainerClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Analytics tab is "active" (underlined) when the analytics page is showing —
  // the same active-tab treatment the Shop Floor button uses.
  const isAnalyticsActive = location.pathname === "/Analytics";

  // Analytics is an opt-in feature gated by the Key Vault secret AnalyticsEnabled.
  // Hide the tab entirely unless it's turned on (the route is guarded too).
  const analyticsEnabled = isAnalyticsEnabled();

  // Account dropdown (username click opens a menu instead of logging out directly)
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // --------- Store state needed for this header -----------
  const user = useStoreState((s) => s.user);
  const isManagerMode = useStoreState((s) => s.appSettings.isManagerMode);
  const setIsManagerMode = useStoreActions((a) => a.appSettings.setIsManagerMode);
  const setIsActiveJobMode = useStoreActions((a) => a.appSettings.setIsActiveJobMode);
  
const jobs = useStoreState((s) => s.appSettings.jobs);

const completedCount = useMemo(() => {
  if (!jobs || jobs.length === 0) return 0;
  return jobs.filter(j => j.operation === "COMPLETED").length;
}, [jobs]);


  if(defaultOrganization == null){
  defaultOrganization = user.info.whoto;
  }
  

  // Derive role access from user.info.complexity bits (fallback to user.complexity if needed)
  const { canSeeManager, canSeeShopfloor } = useMemo(() => {
    // Prefer nested info if present, else fallback
    const complexityRaw =
      (user as any)?.info?.complexity ??
      (user as any)?.complexity ??
      0;

    const complexity = Number(complexityRaw) || 0;

    const hasManager = (complexity & MANAGER_BIT) === MANAGER_BIT;
    const hasShop = (complexity & SHOPFLOOR_BIT) === SHOPFLOOR_BIT;

    // If you later expose explicit booleans, you could do:
    // const hasManager = !!((user as any)?.info?.manager ?? ((complexity & MANAGER_BIT) === MANAGER_BIT));
    // const hasShop = !!((user as any)?.info?.shopFloor ?? ((complexity & SHOPFLOOR_BIT) === SHOPFLOOR_BIT));

    return { canSeeManager: hasManager, canSeeShopfloor: hasShop };
  }, [user]);

  /**
   * An ANALYTICS-ONLY user: active, holding neither bit, and confined to Analytics by
   * `PrivateRoute` (see `utility/analyticsOnlyAccess.ts`).
   *
   * The Manager and Shop Floor tabs already hide themselves for them — those are gated on the
   * bits above — but Images, Documents and the Settings gear are not, and every one of them
   * navigates to a screen PrivateRoute now bounces straight back to Analytics. A control that
   * visibly does nothing is worse than one that is not there, so they are hidden rather than
   * left to no-op.
   *
   * ⚠️ HIDING IS COSMETIC. What keeps an Analytics-only user out of those screens is the route
   * guard, and what keeps them out of other people's reports is the BFF
   * (`SavedReportsRepository.GetVisible` returns own + shared, under row-level security).
   */
  const isAnalyticsOnlyUser = userIsAnalyticsOnly(user);

  // --------- Handlers ----------------------
  const handleLogoClick = () => {
    if (onLogoContainerClick) onLogoContainerClick();
    else navigate("/home");
  };

  const goDocuments = () => navigate("/documents");
  const goImage = () => navigate("/images");
  const goSystem = () => navigate("/system");
  const goAnalytics = () => navigate("/Analytics");
  const doLogout = () => navigate("/logout");

  const doChooseOrgClick = () => navigate("/choose-organization");

  const isCorpUser = ["CORP", "CORP2"].includes(
  (user as any)?.info?.whoto
);

  // NEW: toggle + navigate (guarded by visibility)
  const handleManagerClick = () => {
    if (!canSeeManager) return;
    setIsManagerMode(true);
    setIsActiveJobMode(true);
    navigate("/home");
  };

  const handleShopFloorClick = () => {
    if (!canSeeShopfloor) return;
    setIsManagerMode(false);
    navigate("/home");
  };

  // The completed-count badge rides on the Manager tab, but only while Manager is
  // NOT the current mode — in Manager mode the count is shown by the
  // Active/Completed toggle on the page itself instead.
  const showCompletedBadge = !isManagerMode && completedCount > 0;

  // Swap wrappers based on mode (keep your existing classes). On the Analytics
  // page neither Shop Floor nor Manager is the active tab, so both drop their
  // underline — only the Analytics tab is underlined.
  const shopWrapperClass = !isManagerMode && !isAnalyticsActive
    ? "csm-shop-floor-wrapper"
    : "csm-manager-wrapper";

  const managerWrapperClass = isManagerMode && !isAnalyticsActive
    ? "csm-shop-floor-wrapper"
    : "csm-manager-wrapper";

  // Optionally: if the current mode isn't permitted by the user's roles,
  // you can auto-correct the mode here. Commented to avoid side-effects:
  // useEffect(() => {
  //   if (isManagerMode && !canSeeManager && canSeeShopfloor) setIsManagerMode(false);
  //   if (!isManagerMode && !canSeeShopfloor && canSeeManager) setIsManagerMode(true);
  // }, [isManagerMode, canSeeManager, canSeeShopfloor, setIsManagerMode]);

  
  return (
    <div className="csm-header">
      {/* LEFT: Logo + app names */}
      <div className="logo-parent">
        <div
          className="logo"
          onClick={handleLogoClick}
          role="button"
          aria-label="Home"
          tabIndex={0}
        >
          <img className="logo-child" src={LOGO_SRC} alt="" />
        </div>

        <div className="frame-parent">
          <div
            style={{
              color: "#36424A",   // Figma; was "black"
              fontSize: 20,
              fontFamily: "Noto Sans",
              fontWeight: "300",
              // "33px", not 33. React leaves lineHeight unitless, so `33` meant
              // 33 × the 20px font size = a 660px line box. It was invisible on
              // desktop only because .csm-header is a fixed 72px tall and never
              // clips, so the glyphs still centred where they should. The moment
              // the header is allowed to size to its content (which it is on a
              // phone, to wrap onto two rows) that line box makes it 660px tall.
              lineHeight: "33px",
              wordWrap: "break-word",
            }}
          >
            CSM
          </div>

          {/* Manager button (only if user has Manager flag) */}
          {canSeeManager && (
            <>
            <div className={managerWrapperClass}>
              <div
  className="csm-manager"
  role="button"
  tabIndex={0}
  aria-pressed={isManagerMode}
  data-csm-step="header.manager-tab"
  onClick={handleManagerClick}
  onKeyDown={(e) => e.key === "Enter" && handleManagerClick()}
  title="Manager"
>
  Manager
  {showCompletedBadge && (
    <span className="sfh-csm-toggle__badge">
      {" "}{" "}{" "}{" "}{completedCount}
    </span>
  )}
</div>
            </div>
            {/* Holds the badge's width open while the badge is absent. That 24px
                is the ONLY thing in the header's top row that differs between
                Manager and Shop Floor mode, and on a phone the row has just a
                few pixels of slack — so the badge appearing was enough to push
                "Analytics" onto a second line, making the whole header (and every
                utility icon beneath it) jump 18px each time you switched modes.

                It sits OUTSIDE the tab wrapper on purpose. Inside, it padded the
                wrapper's border-bottom — the active-tab underline — so the
                underline ran ~24px past the word "Manager". The two never collide
                in the original design, because the badge only shows while Manager
                is the INACTIVE tab, whose underline is transparent.

                Zero-width on desktop (`display: none`), so nothing there moves. */}
            {!showCompletedBadge && (
              <span className="sfh-csm-toggle__badge-reserve" aria-hidden="true" />
            )}
            </>
          )}

          {/* Shop Floor button (only if user has Shopfloor flag) */}
          {canSeeShopfloor && (
            <div className={shopWrapperClass}>
              <div
                className="csm-manager"
                role="button"
                tabIndex={0}
                aria-pressed={!isManagerMode}
                data-csm-step="header.shopfloor-tab"
                onClick={handleShopFloorClick}
                onKeyDown={(e) => e.key === "Enter" && handleShopFloorClick()}
                title="Shop Floor"
              >
                Shop Floor
              </div>
            </div>
          )}

          {/* Analytics button — same font + active-underline treatment as the tabs
              above, underlined while the analytics page is showing. Hidden unless
              the AnalyticsEnabled Key Vault flag is on. */}
          {analyticsEnabled && (
            <div className={isAnalyticsActive ? "csm-shop-floor-wrapper" : "csm-manager-wrapper"}>
              <div
                className="csm-manager"
                role="button"
                tabIndex={0}
                aria-pressed={isAnalyticsActive}
                data-csm-step="header.analytics-tab"
                onClick={goAnalytics}
                onKeyDown={(e) => e.key === "Enter" && goAnalytics()}
                title="Analytics"
              >
                Analytics
              </div>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT: utilities only (dropdowns removed) */}
      <div className="button-link-parent">
        {/* Images and Documents are shop-floor screens. Hidden from an Analytics-only user,
            whom PrivateRoute would bounce back to Analytics — see isAnalyticsOnlyUser above. */}
        {!isAnalyticsOnlyUser && (
          <div
            className="utility-link"
            data-csm-step="header.images"
            onClick={goImage}
            role="button"
            title="Images"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && goImage()}
          >
            <img className="user-icon" alt="" src={IMAGE_SRC} />
            <div className="select-warehouse"> Images</div>
          </div>
        )}
        {!isAnalyticsOnlyUser && (
          <div
            className="utility-link"
            data-csm-step="header.documents"
            onClick={goDocuments}
            role="button"
            title="Documents"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && goDocuments()}
          >
            <img className="user-icon" alt="" src={DOC_SRC} />
            <div className="select-warehouse"> Documents</div>
          </div>
        )}
        {/* User name (click => account dropdown, not immediate logout) */}
        <div
          className="utility-link csm-user-menu"
          data-csm-step="header.account"
          role="button"
          title="Account"
          aria-haspopup="menu"
          aria-expanded={userMenuOpen}
          tabIndex={0}
          onClick={() => setUserMenuOpen((o) => !o)}
          onKeyDown={(e) => e.key === "Enter" && setUserMenuOpen((o) => !o)}
          style={{ position: "relative" }}
        >
          <img className="user-icon" alt="" src={USER_SRC} />
          <div className="select-warehouse">
            {(user as any)?.info?.name ?? (user as any)?.fullName ?? "User"}
          </div>

          {userMenuOpen && (
            <>
              {/* click-away backdrop */}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setUserMenuOpen(false);
                }}
                style={{ position: "fixed", inset: 0, zIndex: 999 }}
              />
              <div
                role="menu"
                className="csm-user-menu__dropdown"
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: 8,
                  background: "#fff",
                  border: "1px solid #c4c7c8",
                  borderRadius: 4,
                  minWidth: 160,
                  zIndex: 1000,
                }}
              >
                {/* caret pointing up at the user name: border triangle + white fill */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: -7,
                    right: 16,
                    width: 0,
                    height: 0,
                    borderLeft: "7px solid transparent",
                    borderRight: "7px solid transparent",
                    borderBottom: "7px solid #c4c7c8",
                  }}
                />
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: -6,
                    right: 17,
                    width: 0,
                    height: 0,
                    borderLeft: "6px solid transparent",
                    borderRight: "6px solid transparent",
                    borderBottom: "6px solid #fff",
                  }}
                />
                <div
                  role="menuitem"
                  tabIndex={0}
                  onClick={() => {
                    setUserMenuOpen(false);
                    doLogout();
                  }}
                  onKeyDown={(e) =>
                    e.key === "Enter" && (setUserMenuOpen(false), doLogout())
                  }
                  style={{
                    padding: "10px 14px",
                    cursor: "pointer",
                    textAlign: "right",
                    fontWeight: 300,
                    fontFamily: "Noto Sans, sans-serif",
                  }}
                >
                  Log out
                </div>
              </div>
            </>
          )}
        </div>

        {isCorpUser && (
  <div
    className="utility-link"
    data-csm-step="header.org-switcher"
    onClick={doChooseOrgClick}
    role="button"
    title="Change Organization"
    tabIndex={0}
    onKeyDown={(e) => e.key === "Enter" && doChooseOrgClick()}
  >
    <div className="select-corporation">
      {defaultOrganization}
    </div>
  </div>
)}


        {/* System link visible in Manager mode (unchanged), except for an Analytics-only user:
            `isManagerMode` is a persisted VIEW, not a permission, so a viewer whose session
            happens to carry it would otherwise be shown a gear that bounces. */}
        {isManagerMode && !isAnalyticsOnlyUser && (
          <div
            className="utility-link"
            data-csm-step="header.system-gear"
            onClick={goSystem}
            role="button"
            title="Settings"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && goSystem()}
          >
            <img className="user-icon csm-gear-icon" alt="" src={SYSTEM_SRC} />
            <div className="select-warehouse"></div>
          </div>
        )}

        {/* The per-screen "?" — one press starts this screen's walkthrough. It used to float in
            the bottom-right corner over whatever the user was working on; it lives here now so
            help is in ONE place on every screen, beside the other utilities.

            It is self-gating (feature flag, the path→screen lookup, the user's access to that
            screen, and whether the screen has a tour), so it renders nothing of its own accord
            on a screen it has nothing to say about — this mount needs no condition of its own.

            ⚠️ ICON ONLY, NO LABEL, and that is the note above about the badge restated: the
            header's rows have only a few pixels of slack on a phone, and a word here would push
            a utility off the row exactly as the completed-count badge pushed "Analytics" off the
            branding row. It is announced by its aria-label instead. */}
        <ScreenHelpButton />

        {/* Info (about) */}
        <div className="info-wrapper" role="button" tabIndex={0} aria-label="About">
  

  {/* Version comes from the AppVersion Key Vault secret, not source. */}
  <Tooltip text={getAppVersionLabel()}>
    <img className="path-346-icon" alt="" src={INFO_SRC} />
  </Tooltip>
</div>
      </div>
    </div>
  );
};

export default CSMShopfloorHeader;